import { useState } from "react";
import { useTranslate } from "@refinedev/core";

import { requestMagicCode, verifyMagicCode } from "../../authProvider";

// fix/foster-login-redesign: foster passwordless magic-code login, rendered in the user-provided
// "pleeggezinnen-login-react" visual (the `.login-card` markup + foster-login.css), wired to the real
// two-step flow against POST /api/auth/magic/{request,verify}. The handoff was a static code-step
// mockup; this restores the email step, anti-enumeration, i18n, rate-limit handling and resend.
export function FosterMagicLogin({ onAuthenticated }: { onAuthenticated: () => void }) {
  const t = useTranslate();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [success, setSuccess] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  // Test/acc only: the API exposes the minted code (fawkes.dev.expose-magic-codes) because those
  // environments deliver no mail; show it right on the code step.
  const [devCode, setDevCode] = useState<string | undefined>();

  // The code-sent notice echoes the typed email — anti-enumeration holds: a neutral "if … is known"
  // phrasing wrapping whatever address was typed (never a confirmation that it exists).
  function codeSentNotice(): string {
    return `${t("auth.foster.codeSentPrefix")}${email}${t("auth.foster.codeSentSuffix")}`;
  }

  // Numeric-only, capped at 6 (matches the legacy six-digit field).
  function onCodeChange(value: string) {
    setCode(value.replace(/\D/g, "").slice(0, 6));
  }

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    const result = await requestMagicCode(email);
    setBusy(false);
    if (result.ok) {
      // Always advance — the API answers identically for known + unknown emails (no enumeration).
      setNotice(codeSentNotice());
      setDevCode(result.devCode);
      setStep("code");
    } else {
      setError(t(`auth.error.${result.message}`));
    }
  }

  // Re-request a code for the SAME email without leaving the code step.
  async function resend() {
    setError(undefined);
    setBusy(true);
    const result = await requestMagicCode(email);
    setBusy(false);
    if (result.ok) {
      setNotice(codeSentNotice());
      setDevCode(result.devCode);
    } else {
      setError(t(`auth.error.${result.message}`));
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setError(undefined);
    if (!/^\d{6}$/.test(code)) {
      setError(t("auth.error.codeFormat"));
      return;
    }
    setBusy(true);
    const result = await verifyMagicCode(email, code);
    setBusy(false);
    if (result.ok) {
      setSuccess(t("auth.foster.welcomeBack"));
      onAuthenticated();
    } else {
      setError(t(`auth.error.${result.message}`));
    }
  }

  return (
    <form className="login-card" onSubmit={step === "email" ? submitEmail : submitCode} noValidate>
      <div className="card-icon">
        <span aria-hidden="true">{step === "email" ? "♥" : "🔒"}</span>
      </div>

      <p className="eyebrow">{t("auth.foster.eyebrow")}</p>
      <h1>{step === "email" ? t("auth.foster.title") : t("auth.foster.codeTitle")}</h1>

      {step === "email" ? (
        <>
          <p className="intro">{t("auth.foster.emailLede")}</p>

          <label className="field-label" htmlFor="fosterEmail">
            {t("auth.field.email")}
          </label>
          <div className="input-wrapper">
            <span className="input-icon" aria-hidden="true">✉</span>
            <input
              id="fosterEmail"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-label={t("auth.field.email")}
            />
          </div>

          {error ? (
            <p className="form-error" role="alert">
              {error}
            </p>
          ) : null}

          <button type="submit" className="primary-button" disabled={busy}>
            {busy ? t("auth.foster.requesting") : t("auth.foster.requestCode")}
            <span aria-hidden="true">→</span>
          </button>
        </>
      ) : (
        <>
          <p className="intro" aria-live="polite">
            {notice ?? t("auth.foster.codeLede")}
          </p>

          {devCode ? (
            <p className="dev-code" role="status">
              {t("auth.foster.devCode")} <strong>{devCode}</strong>
            </p>
          ) : null}

          <label className="field-label" htmlFor="fosterCode">
            {t("auth.field.code")}
          </label>
          <div className="input-wrapper">
            <span className="input-icon" aria-hidden="true">🔒</span>
            <input
              id="fosterCode"
              name="loginCode"
              type="text"
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={(e) => onCodeChange(e.target.value)}
              aria-label={t("auth.field.code")}
            />
          </div>

          {error ? (
            <p className="form-error" role="alert">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="form-success" role="status">
              {success}
            </p>
          ) : null}

          <button type="submit" className="primary-button" disabled={busy}>
            {busy ? t("auth.foster.verifying") : t("auth.foster.verify")}
          </button>

          <div className="links">
            <button type="button" onClick={resend} disabled={busy}>
              {busy ? t("auth.foster.resending") : t("auth.foster.resend")}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep("email");
                setCode("");
                setError(undefined);
                setNotice(undefined);
                setDevCode(undefined);
              }}
            >
              {t("auth.foster.changeEmail")}
            </button>
          </div>
        </>
      )}
    </form>
  );
}
